import type { VoidFunctionComponent } from 'react';
import { useMemo } from 'react';
import * as _ from 'lodash';
import styles from './RegulatoryComplianceToggles.module.less';
import Toggle from '../../../../../../generic/Toggle';
import type { SelectOption, SetState } from '../../../../../../../types';
import type { ReportOptions } from '../../../../shared/types';
import {
  ntiaflatIncludeOptions,
  ntiaflatIncludeValues,
  ntiaLicenseOptions,
  ntiaLicenseUncheckValues,
  ntiaMetadataCheckboxOptions,
  ntiaMetadataCheckboxValues,
} from '../../constants';
import type { DependencyMetadataFilterOption } from '../../types';

type Props = {
  setFlatIncludeValues: SetState<SelectOption[]>;
  setDependencyMetadataCheckboxValues: SetState<SelectOption[]>;
  setIncludeCheckboxLicenseValues: SetState<SelectOption[]>;
  flatIncludeValues: SelectOption[];
  includeCheckboxLicenseValues: SelectOption[];
  dependencyMetadataCheckboxValues: SelectOption[];
};

/**
 * Component for toggling regulatory compliance options
 * following this spreadsheet: https://docs.google.com/spreadsheets/d/1968QNZgla7XEZjwkXu-4IK6Jpg7stVIonmofa8OOfoM/edit?gid=2100738777#gid=2100738777
 */
const RegulatoryComplianceToggles: VoidFunctionComponent<Props> = ({
  setFlatIncludeValues,
  setDependencyMetadataCheckboxValues,
  setIncludeCheckboxLicenseValues,
  flatIncludeValues: selectedFlatIncludeValues,
  includeCheckboxLicenseValues: selectedLicenseValues,
  dependencyMetadataCheckboxValues: selectedMetadataCheckboxValues,
}) => {
  // Convert arrays to Sets for efficient checks
  const selectedFlatIncludeValuesSet = useMemo(
    () => new Set(selectedFlatIncludeValues.map(({ value }) => value)),
    [selectedFlatIncludeValues],
  );
  const selectedLicenseValuesSet = useMemo(
    () => new Set(selectedLicenseValues.map(({ value }) => value)),
    [selectedLicenseValues],
  );
  const selectedMetadataCheckboxValuesSet = useMemo(
    () => new Set(selectedMetadataCheckboxValues.map(({ value }) => value)),
    [selectedMetadataCheckboxValues],
  );

  // Check conditions using Sets
  const isFlatIncludeChecked = ntiaflatIncludeOptions.every(({ value }) => selectedFlatIncludeValuesSet.has(value));
  const isLicenseChecked = ntiaLicenseOptions.every(({ value }) => selectedLicenseValuesSet.has(value));
  const isMetadataChecked = ntiaMetadataCheckboxOptions.every(({ value }) =>
    selectedMetadataCheckboxValuesSet.has(value),
  );
  const isToggleChecked = isFlatIncludeChecked && isLicenseChecked && isMetadataChecked;

  return (
    <div className={styles.regulatoryComplianceToggles}>
      <div className={styles.label}>Regulatory Compliance Toggles</div>
      <div className={styles.toggle}>
        <Toggle
          checked={isToggleChecked}
          onChange={(isChecked) => {
            if (isChecked) {
              setFlatIncludeValues((previousValues) => {
                return _.uniqBy([...previousValues, ...ntiaflatIncludeOptions], ({ value }) => value);
              });
              setIncludeCheckboxLicenseValues((previousValues) => {
                return _.uniqBy([...previousValues, ...ntiaLicenseOptions], ({ value }) => value);
              });
              setDependencyMetadataCheckboxValues((previousValues) => {
                return _.uniqBy([...previousValues, ...ntiaMetadataCheckboxOptions], ({ value }) => value);
              });
            } else {
              setFlatIncludeValues((prevState) =>
                prevState.filter(({ value }) => !ntiaflatIncludeValues.has(value as keyof ReportOptions)),
              );
              setIncludeCheckboxLicenseValues((prevState) =>
                prevState.filter(({ value }) => !ntiaLicenseUncheckValues.has(value as keyof ReportOptions)),
              );
              setDependencyMetadataCheckboxValues((prevState) =>
                prevState.filter(
                  ({ value }) => !ntiaMetadataCheckboxValues.has(value as DependencyMetadataFilterOption),
                ),
              );
            }
          }}
        />
        <div className={styles.label}>Check all NTIA/FDA fields</div>
      </div>
      <div className={styles.description}>
        You can require adherence to regulatory specifications. Currently the FDA follows the NTIA
        requirements.
      </div>
    </div>
  );
};

export default RegulatoryComplianceToggles;
